import { call, put, all, takeEvery, select } from 'redux-saga/effects'

import { deleteItemById } from '../../helpers/reducerHelpers'
import { showToasts } from '../../helpers/toasts'

import {
  GET_MEMBERS_LIST,
  getMembersListSuccess,
  getMembersListFailure,
  GET_INVITES_LIST,
  getInvitesListSuccess,
  getInvitesListFailure,
  CREATE_MEMBER,
  createMemberSuccess,
  createMemberFailure,
  UPDATE_MEMBER_ROLE,
  updateMemberRoleSuccess,
  updateMemberRoleFailure,
  CREATE_INVITE,
  createInviteSuccess,
  createInviteAutoApprovedSuccess,
  createInviteFailure,
  ACCEPT_INVITE,
  acceptInviteSuccess,
  acceptInviteFailure,
  RESEND_INVITE,
  resendInviteSuccess,
  resendInviteFailure,
  DELETE_INVITE,
  deleteInviteSuccess,
  deleteInviteFailure,
  UPDATE_MEMBER_STATUS,
  updateMemberStatusSuccess,
  updateMemberStatusFailure,
  CREATE_BETA_INVITE,
  createBetaInviteSuccess,
  createBetaInviteFailure,
  ACCEPT_BETA_INVITE,
  acceptBetaInviteSuccess,
  acceptBetaInviteFailure
} from '../actions/team'
import {
  createInviteService,
  acceptInviteService,
  createMemberService,
  deleteInviteService,
  getInvitesListService,
  getMembersListService,
  resendInviteService,
  updateMemberRoleService,
  updateMemberStatusService,
  createBetaInviteService,
  acceptBetaInviteService
} from '../services/team'
import { clearUpdateCurrentUserProfile, updateCurrentUserProfileSuccess } from '../actions/app'
import { submitLogin } from '../actions/cognito'
import { getCurrentUserSelector } from '../selectors/app'

import { TOAST_TYPE } from '../../constants/other'
import { EMAIL } from '../../pages/Unauthorized/Login/fields'
import { PASSWORD } from '../../pages/Unauthorized/CreatePassword/fields'

function* teamMembersWatcher() {
  yield all([
    // PLOP_APPEND_PATTERN_ANCHOR_WATCHER
    takeEvery(GET_MEMBERS_LIST, getMembersListWorker),
    takeEvery(GET_INVITES_LIST, getInvitesListWorker),
    takeEvery(CREATE_MEMBER, createMemberWorker),
    takeEvery(CREATE_INVITE, createInviteWorker),
    takeEvery(ACCEPT_INVITE, acceptInviteWorker),
    takeEvery(UPDATE_MEMBER_ROLE, updateMemberRoleWorker),
    takeEvery(UPDATE_MEMBER_STATUS, updateMemberStatusWorker),
    takeEvery(DELETE_INVITE, deleteInviteWorker),
    takeEvery(RESEND_INVITE, resendInviteWorker),
    takeEvery(CREATE_BETA_INVITE, createBetaInviteWorker),
    takeEvery(ACCEPT_BETA_INVITE, acceptBetaInviteWorker)
  ])
}

// PLOP_APPEND_PATTERN_ANCHOR_WORKER

function* getMembersListWorker({ params }) {
  try {
    const response = yield call(getMembersListService, params)
    yield put(getMembersListSuccess(response))
  } catch (e) {
    yield put(getMembersListFailure(e))
  }
}

function* getInvitesListWorker({ params }) {
  try {
    const response = yield call(getInvitesListService, params)
    yield put(getInvitesListSuccess(response))
  } catch (e) {
    yield put(getInvitesListFailure(e))
  }
}

function* createMemberWorker({ memberData }) {
  try {
    const response = yield call(createMemberService, memberData)
    yield put(createMemberSuccess(response))
  } catch (e) {
    yield put(createMemberFailure(e))
  }
}

function* createInviteWorker({ inviteData }) {
  try {
    const response = yield call(createInviteService, inviteData)

    // sometimes user can be auto-approved, and we push it inside members list
    // when that happens, response is returned in array format with 1 element
    if (Array.isArray(response)) {
      yield put(createInviteAutoApprovedSuccess(response[0]))
    } else {
      yield put(createInviteSuccess(response))
    }
  } catch (e) {
    yield put(createInviteFailure(e))
  }
}

function* acceptInviteWorker({ inviteData, submitLoginOnSuccess, removeInviteOnSuccess }) {
  try {
    const response = yield call(acceptInviteService, { ...inviteData, [PASSWORD]: inviteData[PASSWORD] })
    yield put(acceptInviteSuccess())

    if (submitLoginOnSuccess) {
      yield put(
        submitLogin({
          [EMAIL]: response[EMAIL],
          [PASSWORD]: inviteData[PASSWORD]
        })
      )
    }

    if (removeInviteOnSuccess) {
      const currentUser = yield select(getCurrentUserSelector)

      const { account_invites: accountInvites } = currentUser

      const filteredAccountInvites = accountInvites.filter(item => item.token !== inviteData.token)

      yield put(updateCurrentUserProfileSuccess({ ...currentUser, account_invites: filteredAccountInvites }))
      yield put(clearUpdateCurrentUserProfile())
    }
  } catch (e) {
    // code is added as key for parsing errors inside the ProgressButton
    yield put(acceptInviteFailure({ ...e, code: 'encryption_error' }))
  }
}

function* updateMemberRoleWorker({ id, role }) {
  try {
    const response = yield call(updateMemberRoleService, id, role)
    yield put(updateMemberRoleSuccess(response))
  } catch (e) {
    yield put(updateMemberRoleFailure(e))
  }
}

function* updateMemberStatusWorker({ id, status }) {
  const isActive = status === 'active'

  try {
    yield call(updateMemberStatusService, id, status)
    yield put(updateMemberStatusSuccess({ id, is_active: !isActive }))
  } catch (e) {
    yield put(updateMemberStatusFailure(e))
  }
}

function* deleteInviteWorker({ id, removeInviteOnSuccess }) {
  try {
    yield call(deleteInviteService, id)
    yield put(deleteInviteSuccess(id))

    if (removeInviteOnSuccess) {
      const currentUser = yield select(getCurrentUserSelector)

      const { account_invites: accountInvites } = currentUser

      yield put(
        updateCurrentUserProfileSuccess({ ...currentUser, account_invites: deleteItemById(accountInvites, id) })
      )
      yield put(clearUpdateCurrentUserProfile())
    }
  } catch (e) {
    yield put(deleteInviteFailure(e))
  }
}

function* resendInviteWorker({ id }) {
  try {
    yield call(resendInviteService, id)
    yield put(resendInviteSuccess(id))
  } catch (e) {
    yield put(resendInviteFailure(e))
  }
}

function* createBetaInviteWorker({ email }) {
  try {
    const response = yield call(createBetaInviteService, email)
    yield put(createBetaInviteSuccess(response))

    const currentUser = yield select(getCurrentUserSelector)

    const { invites_quota_left: invitesQuotaLeft } = currentUser

    // decrease invitesQuotaLeft on successful request sending
    yield put(updateCurrentUserProfileSuccess({ ...currentUser, invites_quota_left: invitesQuotaLeft - 1 }))
    yield put(clearUpdateCurrentUserProfile())

    // show success toast to the user
    showToasts({
      type: TOAST_TYPE.success,
      message: 'Invite was successfully sent'
    })
  } catch (e) {
    yield put(createBetaInviteFailure(e))
  }
}

function* acceptBetaInviteWorker({ inviteData, submitLoginOnSuccess }) {
  try {
    const response = yield call(acceptBetaInviteService, { ...inviteData, [PASSWORD]: inviteData[PASSWORD] })
    yield put(acceptBetaInviteSuccess())

    if (submitLoginOnSuccess) {
      yield put(
        submitLogin({
          [EMAIL]: response[EMAIL],
          [PASSWORD]: inviteData[PASSWORD]
        })
      )
    }
  } catch (e) {
    // code is added as key for parsing errors inside the ProgressButton
    yield put(acceptBetaInviteFailure({ ...e, code: 'encryption_error' }))
  }
}

export default teamMembersWatcher
